import {Container, Modal, Loader, Box, Space, Code, Collapse} from "@mantine/core";
import {Group, Text, rem, Paper, Image} from '@mantine/core';
import {IconUpload, IconFile, IconX} from '@tabler/icons-react';
import {Dropzone} from '@mantine/dropzone';
import {useFileUpload} from '../hooks/useFileUpload';
import {Button, Stack} from "@mantine/core";
import {IconPhotoDown, IconWindowMaximize} from '@tabler/icons-react';
import {useDisclosure, useMediaQuery} from "@mantine/hooks";
import {PageTitleText} from "./PageTitleText";
import {NormalText} from "./NormalText";

export function BoxAndImage() {
    // isPending means the file is being processed
    // image is the returned image
    const {
        isPending,
        isError,
        error: uploadError,
        data: image,
        mutate: uploadFile
    } = useFileUpload();

    const [opened, {open, close}] = useDisclosure(false);
    const [howToOpened, {toggle}] = useDisclosure(false);

    // changes how the buttons are laid out, responsive to window size
    const isBig = useMediaQuery('(min-width: 48em)');

    return (
        <Container>
            <Paper shadow="md" radius="lg" p="xl">
                <PageTitleText>
                    CGR Demo
                </PageTitleText>
                <Space h="md"/>
                <Box>
                    <Button justify="center" onClick={toggle} size="compact-md">Show/Hide Instructions</Button>
                </Box>
                <Collapse in={howToOpened}>
                    <Space h="md"/>
                    <NormalText>
                        This demo represents a small but vital part of what LAGG can do.
                        <Space h="lg"/>
                        Specifically, it computes the final step: using a list of k-mer counts to generate an image
                        using the Chaos Game Representation (CGR) algorithm.
                        <Space h="lg"/>
                        The input file for this demo must be a text (<Code>.txt</Code>)
                        file that contains the k-mer counts for genome data.<br/>

                        Each line in the file must contain a k-mer then the count number, separated with a
                        space.
                        <Space h="lg"/>
                        For example, the first line may appear as "CTAG 41031" indicating the 4-mer CTAG was found
                        41,031 times in the genome.<br/>
                        <Space h="xl"/>
                        To use this demo, each line of the text file input must be of the format:
                        <Space h="xs"/>
                        <Code style={{
                            whiteSpace: 'pre-wrap',
                            display: 'flex',
                            gap: '0.3rem',
                            fontSize: '1.2rem'
                        }}>
                            [genome string sequence] [# of instances in the genome]
                        </Code>
                        <Space h="xl"/>
                        For example, a few lines of a k-mer file count might look like:
                        <Space h="md"/>
                        <Code block
                              style={{
                                  whiteSpace: 'pre-wrap',
                                  display: 'flex',
                                  gap: '0.3rem',
                                  fontSize: '1.2rem'
                              }}>
                            AGGTCAATAC 119 <br/>
                            TTATCAATCC 150 <br/>
                            TCCTGTCTCC 884
                        </Code>
                        <Space h="xl"/>
                        The demo will generate a CGR graph, which can be enlarged to examine in closer detail,
                        or downloaded to your PC as a <Code>.jpg</Code> file.
                        <Space h="xl"/>
                        <Button onClick={() => {
                            uploadFile([]);
                            toggle();
                        }}
                        >Click here to try it out!</Button>
                    </NormalText>
                </Collapse>
                <Space h="xl"/>
                <Dropzone
                    onDrop={uploadFile}
                    onReject={(files) => console.log('rejected files', files)}
                    maxFiles={1}
                    accept={["text/plain"]}
                    disabled={isPending}
                >
                    <Group justify="center" gap="xl" mih={150} style={{pointerEvents: 'none'}}>
                        <Dropzone.Accept>
                            <IconUpload
                                style={{
                                    width: rem(52),
                                    height: rem(52),
                                    color: 'var(--primary-color)'
                                }}
                                stroke={1.5}
                            />
                        </Dropzone.Accept>
                        <Dropzone.Reject>
                            <IconX
                                style={{
                                    width: rem(52),
                                    height: rem(52),
                                    color: 'var(--mantine-color-red-6)'
                                }}
                                stroke={1.5}
                            />
                        </Dropzone.Reject>
                        <Dropzone.Idle>
                            {isPending ?
                                (
                                    <Loader color="white" type="bars"/>
                                )
                                :
                                <IconFile
                                    style={{
                                        width: rem(52),
                                        height: rem(52),
                                        color: 'var(--mantine-color-dimmed)'
                                    }}
                                    stroke={1.5}
                                />
                            }

                        </Dropzone.Idle>

                        <div>
                            {isPending ?
                                (
                                    <>
                                        <Text size="xl">Your file is being processed...</Text>
                                    </>
                                )
                                :
                                (<>
                                    <Text size="xl" inline>
                                        Drag text file here
                                    </Text>
                                    <Text size="sm" c="dimmed" inline mt={7}>
                                        Or click to navigate to a file to upload
                                    </Text>
                                </>)}
                        </div>
                    </Group>
                </Dropzone>
                {
                    isError ? (
                        <Box pt="xl">
                            <Text size="lg" fw={700} c="red" ta="center">Error with uploaded file:</Text>
                            <Text size="lg" fw={700} c="red" ta="center">{uploadError?.message || ""}</Text>
                            <Text size="lg" fw={700} c="red" ta="center">Please try again.</Text>
                        </Box>
                    ) : null
                }
            </Paper>
            {
                image?.url ? (
                    <Paper shadow="md" radius="lg" p="xl" mt="xl">
                        <Group justify="center" grow={true}>
                            <Stack align="center" justify="center">
                                <Image
                                    src={image.url}
                                    radius="md"
                                    h={rem(350)}
                                    w={rem(350)}
                                    fit="contain"
                                    onClick={open}
                                />
                                <Button.Group orientation={isBig ? "horizontal" : "vertical"}>
                                    <Button
                                        size="xl"
                                        radius="md"
                                        variant="filled"
                                        onClick={open}
                                        leftSection={<IconWindowMaximize/>}
                                    >
                                        Click To Enlarge Photo
                                    </Button>
                                    <Button
                                        size="xl"
                                        radius="md"
                                        variant="filled"
                                        onClick={() => {
                                            // Create a download link
                                            const downloadLink = document.createElement('a');
                                            downloadLink.href = image.url;
                                            downloadLink.download = 'chaosgraph.png';
                                            downloadLink.click();
                                        }}
                                        leftSection={<IconPhotoDown/>}
                                    >
                                        Download Image
                                    </Button>
                                </Button.Group>

                                <Modal
                                    opened={opened}
                                    onClose={close}
                                    transitionProps={{transition: 'fade', duration: 200}}
                                    fullScreen={true}
                                    zIndex={1000000000}
                                >
                                    <Image src={image.url}
                                           h="100%"
                                           w="100%"
                                           fit="contain"
                                    />
                                </Modal>
                            </Stack>
                        </Group>
                    </Paper>
                ) : null
            }

        </Container>
    );

}

